import { endOfDay, parse, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useData as useVikeData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';

import { useDeepMemo } from '@/renderer/utils/hooks/useDeepMemo';
import type { ResultsFilters, ResultsFormFilters } from './FilterTypes';
import Filters from './Filters';
import { ResultsContents } from './ResultsContents';
import { SPORT_IDS, type SportName } from './consts';

const CFB_FILTERS: ResultsFilters = {
  sportId: '1',
  startDate: new Date('2021-07-01'),
  endDate: new Date('2025-01-22'),
  playType: 'full' as const,
};

const NFL_FILTERS: ResultsFilters = {
  sportId: '3',
  startDate: new Date('2021-07-01'),
  endDate: new Date('2025-02-09'),
  playType: 'full' as const,
};

const EMPTYOBJ = {};

export function Results({
  userId: baseUserId,
  title: pageTitle = 'Results',
  baseUrl = '/results',
  breakdown = undefined,
  hideHeader = false,
}: {
  userId: API.Id | 'me' | 'archive';
  baseUrl?: string;
  title?: string;
  breakdown?: string;
  hideHeader?: boolean;
}) {
  const { urlParsed, routeParams } = usePageContext();
  const data = useVikeData() as
    | {
        seasons: API.Season[];
        seasonsSimple: {
          last5: API.Season[];
          first: API.Season;
          last: API.Season;
          selected: API.Season[];
        };
      }
    | undefined;

  const search = useDeepMemo(urlParsed.search || EMPTYOBJ);

  const [userId, _setUserId] = useState(baseUserId);

  const sportId = routeParams.sportId
    ? SPORT_IDS[routeParams.sportId as SportName]
      ? `${SPORT_IDS[routeParams.sportId as SportName]}`
      : routeParams.sportId
    : SPORT_IDS[search.sportId as SportName]
      ? `${SPORT_IDS[search.sportId as SportName]}`
      : search.sportId || 'ras';

  const seasonId = routeParams.seasonId;

  const form = useForm<ResultsFormFilters>({
    defaultValues: {
      seasonId:
        routeParams.seasonId || (search.startDate || search.endDate ? undefined : 'current'),
      // convert 'nfl' in URL to '3' for form
      sportId,
      hidePropsFutures: !!search.playType && search.playType !== 'full',
      gameType: search.gameType || undefined,
    },
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const hidePropsFutures = form.watch('hidePropsFutures');

  const requestFilter = useMemo(() => {
    const res: ResultsFilters = {
      ...search,
      ...routeParams,
      sportId,
      // Supports a comma-separated list (e.g. ?gameType=fcs,extra) so a package
      // results link can scope to several play types at once.
      gameType: search.gameType
        ? (String(search.gameType).split(',').filter(Boolean) as API.GameTypes[])
        : undefined,
      startDate: search?.startDate
        ? startOfDay(parse(search.startDate, 'yyyyMMdd', new Date()))
        : undefined,
      endDate: search?.endDate
        ? endOfDay(parse(search.endDate, 'yyyyMMdd', new Date()))
        : undefined,
      playType:
        (search.playType as API.PlayTypes | 'full' | 'st' | 'pf' | 'hp') ||
        (hidePropsFutures ? ('hp' as const) : undefined),
    };

    if (!res.startDate && !res.endDate && seasonId !== 'all-time') {
      const endDate = data?.seasonsSimple?.selected?.[0]?.endDate;
      res.endDate = endDate ? new Date(endDate) : undefined;

      const startDate =
        data?.seasonsSimple?.selected?.[data?.seasonsSimple?.selected?.length - 1]?.startDate;
      res.startDate = startDate ? new Date(startDate) : undefined;
    }

    return res;
  }, [routeParams, search, data?.seasonsSimple, seasonId, hidePropsFutures, sportId]);

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col overflow-auto">
      {/* <div className="mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8"> */}
      <Filters
        baseUrl={baseUrl}
        form={form}
        hide={!!breakdown}
        hideHeader={hideHeader}
        title={pageTitle}
        sportId={routeParams.sportId || search.sportId}
        seasonId={seasonId}
      />
      <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <ResultsContents
          baseUrl={baseUrl}
          form={form}
          // key={`${sportId}-${playType}-${startDate}-${endDate}-${free}`}
          userId={userId}
          filters={
            breakdown === 'cfb' ? CFB_FILTERS : breakdown === 'nfl' ? NFL_FILTERS : requestFilter
          }
          breakdown={breakdown}
          title={pageTitle}
        />
        <small className="block w-full text-right text-xs italic opacity-25">
          Results may be delayed by several minutes.
        </small>
      </div>
    </div>
  );
}
